import { useState, useCallback } from "react";

export const useTableDialogs = () => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(null);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuData, setMenuData] = useState(null);

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusData, setStatusData] = useState(null);

  const [leadFeedbackOpen, setLeadFeedbackOpen] = useState(false);
  const [leadFeedbackData, setLeadFeedbackData] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteData, setDeleteData] = useState(null);

  const [financierChangeRequestDialog, setFinancierChangeRequestDialog] =
    useState(false);
  const [financierChangeRequestData, setFinancierChangeRequestData] =
    useState(null);

  // ONBOARDING FORM DIALOG - stores its own data
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);

  // Card Addition
  const [cardAdditionOpen, setCardAdditionOpen] = useState(false);
  const [cardAdditionData, setCardAdditionData] = useState(null);

  const [crifReportOpen, setCrifReportOpen] = useState(false);
  const [crifReportData, setCrifReportData] = useState(null);

  // MENU HANDLERS
  const openMenu = useCallback((event, rowData) => {
    setMenuAnchor(event.currentTarget);
    setMenuData(rowData);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuData(null);
  }, []);

  // DETAILS DIALOG HANDLERS
  const openDetailsDialog = useCallback((rowData) => {
    setDetailsData(rowData);
    setDetailsOpen(true);
  }, []);

  const closeDetailsDialog = useCallback(() => {
    setDetailsOpen(false);
    setDetailsData(null);
  }, []);

  const openStatusDialog = useCallback((rowData) => {
    setStatusData(rowData);
    setStatusOpen(true);
  }, []);

  const closeStatusDialog = useCallback(() => {
    setStatusOpen(false);
    setStatusData(null);
  }, []);

  const openLeadFeedbackDialog = useCallback((rowData) => {
    setLeadFeedbackData(rowData);
    setLeadFeedbackOpen(true);
  }, []);

  const closeLeadFeedbackDialog = useCallback(() => {
    setLeadFeedbackOpen(false);
    setLeadFeedbackData(null);
  }, []);

  const openCrifReportDialog = useCallback((rowData) => {
    setCrifReportData(rowData);
    setCrifReportOpen(true);
  }, []);

  const closeCrifReportDialog = useCallback(() => {
    setCrifReportOpen(false);
    setCrifReportData(null);
  }, []);

  const openDeleteDialog = useCallback((rowData) => {
    setDeleteData(rowData);
    setDeleteOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteOpen(false);
    setDeleteData(null);
  }, []);

  const openFinancierChangeRequestDialog = useCallback((rowData) => {
    setFinancierChangeRequestData(rowData);
    setFinancierChangeRequestDialog(true);
  }, []);

  const closeFinancierChangeRequestDialog = useCallback(() => {
    setFinancierChangeRequestDialog(false);
    setFinancierChangeRequestData(null);
  }, []);

  const openOnboardingDialog = useCallback((rowData) => {
    setOnboardingData(rowData);
    setOnboardingOpen(true);
  }, []);

  const closeOnboardingDialog = useCallback(() => {
    setOnboardingOpen(false);
    setOnboardingData(null);
  }, []);

  const openCardAdditionDialog = useCallback((rowData) => {
    setCardAdditionOpen(true);
    setCardAdditionData(rowData);
  }, []);

  const closeCardAdditionDialog = useCallback(() => {
    setCardAdditionData(null);
    setCardAdditionOpen(false);
  }, []);

  const handleRowView = useCallback(() => {
    openDetailsDialog(menuData);
    closeMenu();
  }, [menuData, openDetailsDialog, closeMenu]);

  const handleRowEdit = useCallback(() => {
    openStatusDialog(menuData);
    closeMenu();
  }, [menuData, openStatusDialog, closeMenu]);

  const handleRowDelete = useCallback(() => {
    openDeleteDialog(menuData);
    closeMenu();
  }, [menuData, openDeleteDialog, closeMenu]);

  const handleFinancierChangeRequest = useCallback(() => {
    openFinancierChangeRequestDialog(menuData);
    closeMenu();
  }, [menuData, openFinancierChangeRequestDialog, closeMenu]);

  const handleRowOnboard = useCallback(() => {
    openOnboardingDialog(menuData);
    closeMenu();
  }, [menuData, openOnboardingDialog, closeMenu]);

  const handleCardAddition = useCallback(
    (data) => {
      openCardAdditionDialog(data);
      closeMenu();
    },
    [menuData, openCardAdditionDialog, closeMenu],
  );

  const handleLeadFeedback = useCallback(() => {
    openLeadFeedbackDialog(menuData);
    closeMenu();
  }, [menuData, openLeadFeedbackDialog, closeMenu]);

  const handleCrifReport = useCallback(() => {
    openCrifReportDialog(menuData);
    closeMenu();
  }, [menuData, openCrifReportDialog, closeMenu]);

  return {
    menu: {
      open: Boolean(menuAnchor),
      anchorEl: menuAnchor,
      data: menuData,
      onOpen: openMenu,
      onClose: closeMenu,
    },

    detailsDialog: {
      open: detailsOpen,
      data: detailsData,
      onOpen: openDetailsDialog,
      onClose: closeDetailsDialog,
    },

    statusDialog: {
      open: statusOpen,
      data: statusData,
      onOpen: openStatusDialog,
      onClose: closeStatusDialog,
    },

    leadFeedbackDialog: {
      open: leadFeedbackOpen,
      data: leadFeedbackData,
      onOpen: openLeadFeedbackDialog,
      onClose: closeLeadFeedbackDialog,
    },

    crifReportOpen: {
      open: crifReportOpen,
      data: crifReportData,
      onOpen: openCrifReportDialog,
      onClose: closeCrifReportDialog,
    },

    deleteDialog: {
      open: deleteOpen,
      data: deleteData,
      onOpen: openDeleteDialog,
      onClose: closeDeleteDialog,
    },

    financierChangeRequestDialog: {
      open: financierChangeRequestDialog,
      data: financierChangeRequestData,
      onOpen: openFinancierChangeRequestDialog,
      onClose: closeFinancierChangeRequestDialog,
    },

    onboardingDialog: {
      open: onboardingOpen,
      data: onboardingData,
      onOpen: openOnboardingDialog,
      onClose: closeOnboardingDialog,
    },

    cardAddition: {
      open: cardAdditionOpen,
      data: cardAdditionData,
      onOpen: openCardAdditionDialog,
      onClose: closeCardAdditionDialog,
    },

    rowActions: {
      handleView: handleRowView,
      handleEdit: handleRowEdit,
      handleDelete: handleRowDelete,
      handleOnboard: handleRowOnboard,
      handleCardAddition: handleCardAddition,
      handleFinancierChangeRequest: handleFinancierChangeRequest,

      handleLeadFeedback: handleLeadFeedback,
      handleCrifReport: handleCrifReport,
    },
  };
};

export const useMenuState = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedData, setSelectedData] = useState(null);

  const openMenu = useCallback((event, data) => {
    setAnchorEl(event.currentTarget);
    setSelectedData(data);
  }, []);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
    setSelectedData(null);
  }, []);

  return {
    anchorEl,
    selectedData,
    open: Boolean(anchorEl),
    openMenu,
    closeMenu,
  };
};

export const useDialog = (initialData = null) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(initialData);

  const openDialog = useCallback((newData = null) => {
    if (newData) setData(newData);
    setOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setOpen(false);
    setData(initialData);
  }, [initialData]);

  return {
    open,
    data,
    setData,
    openDialog,
    closeDialog,
  };
};
